import { apiClient } from './api'
import type {
  Service,
  ServiceListItem,
  ServiceCategory,
  ServiceSearchRequest,
  ServiceSearchResponse,
  ServiceReview
} from '@/types/services'

class ServicesService {
  private readonly baseUrl = '/public/services'

  /**
   * Search services with filters and pagination
   */
  async searchServices(request: ServiceSearchRequest): Promise<ServiceSearchResponse> {
    const cleanedParams = this.cleanSearchParams(request)
    console.log('🔍 ServicesService.searchServices - Original request:', request)
    console.log('🔍 ServicesService.searchServices - Cleaned params:', cleanedParams)
    console.log('🔍 ServicesService.searchServices - ProviderId in params:', cleanedParams.providerId)

    return await apiClient.get<ServiceSearchResponse>(`${this.baseUrl}`, {
      params: cleanedParams
    })
  }

  /**
   * Get service by ID
   */
  async getServiceById(id: string): Promise<Service> {
    console.log('🌐 ServicesService fetching service ID:', id)
    console.log('🔗 Using URL:', `${this.baseUrl}/${id}`)
    const service = await apiClient.get<Service>(`${this.baseUrl}/${id}`)
    console.log('📡 ServicesService received response:', service)
    console.log('📄 Documents in response:', service.documents)
    return service
  }

  /**
   * Get service by service code
   */
  async getServiceByCode(serviceCode: string): Promise<Service> {
    console.log('🌐 ServicesService fetching service by code:', serviceCode)
    const service = await apiClient.get<Service>(`${this.baseUrl}/code/${serviceCode}`)
    console.log('📡 ServicesService received response:', service)
    return service
  }

  /**
   * Get services by category
   */
  async getServicesByCategory(categoryId: string): Promise<ServiceListItem[]> {
    return await apiClient.get<ServiceListItem[]>(`${this.baseUrl}/category/${categoryId}`)
  }

  /**
   * Get services by provider with pagination and count
   */
  async getProviderServices(
    providerId: string,
    page: number = 1,
    pageSize: number = 12,
    sortBy: string = 'CreatedAt',
    sortDirection: string = 'desc'
  ): Promise<ServiceSearchResponse> {
    console.log('🔍 ServicesService.getProviderServices - ProviderId:', providerId)
    return await apiClient.get<ServiceSearchResponse>(`${this.baseUrl}/provider/${providerId}`, {
      params: { page, pageSize, sortBy, sortDirection }
    })
  }

  /**
   * Get featured services
   */
  async getFeaturedServices(count: number = 6): Promise<ServiceListItem[]> {
    return await apiClient.get<ServiceListItem[]>(`${this.baseUrl}/featured`, {
      params: { count }
    })
  }

  /**
   * Get service categories
   */
  async getCategories(): Promise<ServiceCategory[]> {
    return await apiClient.get<ServiceCategory[]>(`${this.baseUrl}/categories`)
  }

  /**
   * Get service reviews
   */
  async getServiceReviews(serviceId: string, page: number = 1, pageSize: number = 10): Promise<ServiceReview[]> {
    try {
      return await apiClient.get<ServiceReview[]>(`${this.baseUrl}/${serviceId}/reviews`, {
        params: { 
          page: page.toString(), 
          pageSize: pageSize.toString() 
        }
      })
    } catch (error: any) {
      // For 404 errors (no reviews found), return empty array instead of throwing
      if (error.response?.status === 404) {
        return []
      }
      // For all other errors, let them bubble up
      throw error
    }
  }


  /**
   * Clean search parameters by removing undefined/null values
   * and properly handle arrays for .NET backend
   */
  private cleanSearchParams(params: ServiceSearchRequest): Record<string, any> {
    const cleanedParams: Record<string, any> = {}

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Handle arrays - .NET expects multiple params with same name
        if (Array.isArray(value) && value.length > 0) {
          cleanedParams[key] = value
        } else if (!Array.isArray(value)) {
          cleanedParams[key] = value
        }
      }
    })

    return cleanedParams
  }

  /**
   * Format price for display
   */
  formatPrice(price: number, currencySymbol: string = 'GBP'): string {
    return `${currencySymbol}${price.toFixed(2)}`
  }

  /**
   * Format duration for display
   */
  formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`
    }
    
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    
    if (remainingMinutes === 0) {
      return `${hours}h`
    }
    
    return `${hours}h ${remainingMinutes}min`
  }

  /**
   * Format rating for display
   */
  formatRating(rating: number): string {
    return rating.toFixed(1)
  }

  /**
   * Get rating stars array for display
   */
  getRatingStars(rating: number): Array<'full' | 'half' | 'empty'> {
    const stars: Array<'full' | 'half' | 'empty'> = []
    const fullStars = Math.floor(rating)
    const hasHalfStar = rating - fullStars >= 0.5
    
    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push('full')
    }
    
    // Add half star if needed
    if (hasHalfStar) {
      stars.push('half')
    }
    
    // Fill remaining with empty stars
    while (stars.length < 5) {
      stars.push('empty')
    }
    
    return stars
  }

  /**
   * Get day name from day of week number
   */
  getDayName(dayOfWeek: number): string {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    return days[dayOfWeek] || ''
  }

  /**
   * Format time from TimeOnly string
   */
  formatTime(timeString: string): string {
    try {
      const [hours, minutes] = timeString.split(':')
      const hour24 = parseInt(hours, 10)
      const minute = parseInt(minutes, 10)
      
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
      const period = hour24 >= 12 ? 'PM' : 'AM'
      
      return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`
    } catch (error) {
      console.error('Error formatting time:', timeString, error)
      return timeString
    }
  }
}

export default new ServicesService()